import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-normal whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        // Tixbase buttons (Admin V1 Button, Figma 1:90). Use with the pill-* sizes.
        // before:bg-* is the hover gradient; it fades in (see compoundVariants).
        primary:
          "bg-gradient-primary text-neutral-0 before:bg-gradient-bloom dark:bg-none dark:bg-neutral-0 dark:text-neutral-1000 dark:before:hidden dark:hover:bg-neutral-0/80 disabled:bg-none disabled:bg-button-disabled disabled:text-button-disabled-foreground disabled:opacity-100 dark:disabled:bg-button-disabled dark:disabled:text-button-disabled-foreground",
        "primary-outline":
          "border-button-outline text-foreground before:bg-gradient-primary hover:border-transparent hover:text-neutral-0 disabled:border-button-disabled disabled:text-button-disabled disabled:opacity-100",
        // Hover dims the fill to 80% (Shared hover rule), no gradient.
        contrast:
          "bg-neutral-1000 text-neutral-0 hover:bg-neutral-1000/80 dark:border-button-outline disabled:bg-none disabled:bg-button-disabled disabled:text-button-disabled-foreground disabled:opacity-100",
        // Underline position and thickness come from the font, as in Figma ("from font"), so the
        // line stays thin and close to the text at any size. Hover lightens the label to muted
        // (design lead, 2026-10-16) and underlines it.
        text: "text-foreground decoration-from-font underline-offset-auto [text-underline-position:from-font] hover:text-muted-foreground hover:underline disabled:text-button-disabled disabled:opacity-100",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
        // Tixbase pill sizes: L 48 / M 44 / S 32, Regular labels (type/web/btn-*).
        "pill-lg":
          "h-12 gap-1 rounded-full px-6.5 text-body4 font-normal [&_svg:not([class*='size-'])]:size-5",
        "pill-md": "h-11 gap-1 rounded-full px-6.5 text-body-sm font-normal",
        "pill-sm": "h-8 gap-1 rounded-full px-3.75 text-body-xs font-normal",
        "icon-pill-lg": "size-12 rounded-full [&_svg:not([class*='size-'])]:size-5",
        "icon-pill-md": "size-11 rounded-full",
        "icon-pill-sm": "size-8 rounded-full",
        // Inline label-sized button, e.g. a sortable table header (type/web/sub5).
        inline:
          "h-auto gap-1 rounded-none border-0 px-0 text-sub5 font-semibold [&_svg:not([class*='size-'])]:size-3.75",
      },
    },
    compoundVariants: [
      // Text buttons sit inline: no box, only the label.
      { variant: "text", class: "h-auto rounded-none border-0 px-0" },
      // Hover timing (guidebook p.49): 0.5s, ease-bloom.
      {
        variant: ["primary", "primary-outline", "contrast", "text"],
        class: "transition-colors duration-500 ease-bloom motion-reduce:transition-none",
      },
      // Gradients cannot transition, so the hover gradient is a ::before layer that fades in;
      // isolate + -z-10 keep it under the label.
      {
        variant: ["primary", "primary-outline"],
        class:
          "relative isolate before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-500 before:ease-bloom hover:before:opacity-100 disabled:before:hidden motion-reduce:before:transition-none",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
